import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { Sparkles, RefreshCw, MessageCircle, Waves, BookOpen, Info, Lightbulb } from "lucide-react";
import AICoachChatDialog from "@/components/AICoachChatDialog";
import { LearnStatsStrip } from "@/components/LearnStatsStrip";
import { LearnReviewCard } from "@/components/LearnReviewCard";
import { TrendBanner } from "@/components/Sparkline";

const LS_KEY = "solano_scores_active_v1";

// Pick TODAY's most impactful Mediterranean tip: the category where the user
// is leaving the most points on the table. A maxed-out meat penalty wins.
function medTipKey(med) {
  if (!med || !Array.isArray(med.components) || med.meals_logged === 0) return null;
  const meat = med.components
    .filter((c) => c.sign === "-" && Math.abs(c.points || 0) >= 6)
    .sort((a, b) => (b.value || 0) - (a.value || 0))[0];
  if (meat) return meat.key;
  let best = null, bestGap = 0;
  for (const c of med.components) {
    if (c.sign !== "+") continue;
    const gap = (c.max || 0) - (c.points || 0);
    if (gap > bestGap) { bestGap = gap; best = c.key; }
  }
  return bestGap > 0 ? best : null;
}
const LS_COACH_READ = "solano_coach_read_at";

export const MED_LABELS = {
  it: { olive_oil: "Olio d'oliva", fish: "Pesce", vegetables: "Verdura", fruits: "Frutta",
        legumes: "Legumi", whole_grains: "Cereali integrali", nuts_seeds: "Frutta secca / semi",
        dairy_fermented: "Yogurt / Kefir", red_meat: "Carne rossa", processed_meat: "Insaccati / Salumi" },
  en: { olive_oil: "Olive oil", fish: "Fish", vegetables: "Vegetables", fruits: "Fruit",
        legumes: "Legumes", whole_grains: "Whole grains", nuts_seeds: "Nuts & seeds",
        dairy_fermented: "Yogurt / Kefir", red_meat: "Red meat", processed_meat: "Processed / cured meats" },
  de: { olive_oil: "Olivenöl", fish: "Fisch", vegetables: "Gemüse", fruits: "Obst",
        legumes: "Hülsenfrüchte", whole_grains: "Vollkorn", nuts_seeds: "Nüsse & Samen",
        dairy_fermented: "Joghurt / Kefir", red_meat: "Rotes Fleisch", processed_meat: "Wurst & Aufschnitt" },
  fr: { olive_oil: "Huile d'olive", fish: "Poisson", vegetables: "Légumes", fruits: "Fruits",
        legumes: "Légumineuses", whole_grains: "Céréales complètes", nuts_seeds: "Fruits secs / graines",
        dairy_fermented: "Yaourt / Kéfir", red_meat: "Viande rouge", processed_meat: "Charcuterie" },
};

function healthColors(s) {
  if (s >= 90) return { ring: "#3AA35B", text: "#1E5C2E" };
  if (s >= 80) return { ring: "#5A8B8F", text: "#2C5254" };
  if (s >= 70) return { ring: "#E8951C", text: "#8A5410" };
  if (s >= 60) return { ring: "#E85D1C", text: "#8A3A10" };
  return { ring: "#DC2626", text: "#7F1D1D" };
}
function medColors(s) {
  if (s >= 85) return { ring: "#3AA35B", text: "#1E5C2E" };
  if (s >= 70) return { ring: "#0EA5E9", text: "#0C4A6E" };
  if (s >= 55) return { ring: "#E8951C", text: "#8A5410" };
  if (s >= 40) return { ring: "#E85D1C", text: "#8A3A10" };
  return { ring: "#DC2626", text: "#7F1D1D" };
}
// Color the "value" number based on progress vs. target.
// isPos=true → higher is better (default). isPos=false → lower is better (e.g., red meat).
// Thresholds: reached (green) ≥ 1.0, almost (orange) ≥ 0.6, else red.
function valueColorClass(value, target, isPos = true) {
  const v = Number(value) || 0;
  const tg = Number(target) || 0;
  if (tg <= 0) {
    if (isPos) return "text-slate-700 dark:text-slate-200";
    return v === 0
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-rose-500 dark:text-rose-400";
  }
  const r = v / tg;
  if (isPos) {
    if (r >= 1) return "text-emerald-600 dark:text-emerald-400";
    if (r >= 0.6) return "text-amber-500 dark:text-amber-400";
    return "text-rose-500 dark:text-rose-400";
  }
  // Inverted goal: lower is better
  if (r <= 1) return "text-emerald-600 dark:text-emerald-400";
  if (r <= 1.4) return "text-amber-500 dark:text-amber-400";
  return "text-rose-500 dark:text-rose-400";
}

function healthLabelFor(score, t) {
  if (score >= 90) return t("health.excellent");
  if (score >= 80) return t("health.veryGood");
  if (score >= 70) return t("health.good");
  if (score >= 60) return t("health.fair");
  return t("health.poor");
}

function ScoreRing({ score, colors, size = 44, stroke = 3 }) {
  const r = size / 2 - stroke - 1;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <div className="relative shrink-0" style={{ height: size, width: size }} aria-hidden>
      <svg width={size} height={size} className="-rotate-90 absolute inset-0">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={colors.ring} strokeWidth={stroke} fill="none"
          strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off}
          style={{ transition: "stroke-dashoffset 400ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-heading font-bold tabular-nums" style={{ color: colors.text, fontSize: size >= 50 ? 15 : 13 }}>
          {score}
        </span>
      </div>
    </div>
  );
}

function ChipButton({ active, onClick, testId, iconNode, label, subtitle, badge, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      data-testid={testId}
      className={`relative flex flex-col items-center justify-start gap-1 rounded-2xl border px-1.5 py-2.5 transition select-none min-h-[104px]
                  ${active
                    ? "border-[#5A8B8F] bg-[#EAF3F4] shadow-sm dark:bg-[#1e2f31] dark:border-[#5A8B8F]"
                    : "border-slate-200 bg-white dark:bg-[#1a1d1e] dark:border-slate-700"}
                  ${disabled ? "opacity-45 cursor-not-allowed" : "hover:border-[#5A8B8F]/60 active:scale-95"}`}
    >
      {iconNode}
      {badge != null && badge > 0 && (
        <span
          className="absolute top-1 right-1 flex items-center justify-center"
          data-testid={`${testId}-badge`}
        >
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#E85D1C]/50 animate-ping" aria-hidden />
          <span className="relative min-w-[18px] h-[18px] rounded-full bg-[#E85D1C] text-white text-[10px] font-bold px-1 flex items-center justify-center shadow-sm ring-2 ring-white dark:ring-[#1a1d1e]">
            {badge}
          </span>
        </span>
      )}
      <span className="mt-1 text-[10px] font-semibold text-[#1A1D1E] dark:text-slate-100 uppercase tracking-wide leading-tight text-center truncate max-w-full">
        {label}
      </span>
      {subtitle && (
        <span
          className="text-[9px] font-medium text-slate-500 dark:text-slate-400 leading-tight text-center truncate max-w-full px-0.5"
          data-testid={`${testId}-subtitle`}
        >
          {subtitle}
        </span>
      )}
    </button>
  );
}

export default function DashboardScoresRow({ date }) {
  const { t, lang } = useT();
  const [active, setActive] = useState(() => {
    try {
      const v = localStorage.getItem(LS_KEY);
      if (v === null || v === "") return null;
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? null : n;
    } catch { return null; }
  });

  const [health, setHealth] = useState(null);  const [med, setMed] = useState(null);
  const [trend, setTrend] = useState(null);
  const [coach, setCoach] = useState(null);
  const [learn, setLearn] = useState(null);
  const [learnStats, setLearnStats] = useState(null);
  const [coachLoading, setCoachLoading] = useState(true);
  const [learnLoading, setLearnLoading] = useState(true);
  const [coachRefreshing, setCoachRefreshing] = useState(false);
  const [learnRefreshing, setLearnRefreshing] = useState(false);
  const [quizResult, setQuizResult] = useState(null); // {picked, correct, correct_index, explanation, awarded, points}
  const [quizBusy, setQuizBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [medInfoOpen, setMedInfoOpen] = useState(false);
  const [healthInfoOpen, setHealthInfoOpen] = useState(false);
  const [openSubscore, setOpenSubscore] = useState(null);
  // Which Mediterran-Score row (by category key) is expanded to show the
  // underlying food-log contributions. Only one row open at a time.
  const [openMedRow, setOpenMedRow] = useState(null);
  const [coachReadAt, setCoachReadAt] = useState(() => {
    try { return localStorage.getItem(LS_COACH_READ) || ""; } catch { return ""; }
  });

  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const q = date ? `?date=${date}` : "";
    api.get(`/client/health-score${q}`).then(({ data }) => { if (!cancelled) setHealth(data); }).catch(() => {});
    api.get("/client/mediterranean-score").then(({ data }) => { if (!cancelled) setMed(data); }).catch(() => {});
    api.get("/client/scores/trend?days=7").then(({ data }) => { if (!cancelled) setTrend(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [date, tick]);

  // Keep the scores in sync with fresh water/meal logs: re-poll every 60 s
  // and whenever the app regains focus (was: fetched once → stale values).
  useEffect(() => {
    const iv = setInterval(() => setTick((n) => n + 1), 60000);
    const onVis = () => { if (document.visibilityState === "visible") setTick((n) => n + 1); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCoachLoading(true);
    api.get(`/client/ai-coach/today?lang=${lang || "it"}`)
      .then(({ data }) => { if (!cancelled) setCoach(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCoachLoading(false); });

    setLearnLoading(true);
    api.get(`/client/learn/today?lang=${lang || "it"}`)
      .then(({ data }) => { if (!cancelled) setLearn(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLearnLoading(false); });
    loadLearnStats();

    return () => { cancelled = true; };
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLearnStats = () => {
    api.get(`/client/learn/stats?lang=${lang || "it"}`).then(({ data }) => setLearnStats(data)).catch(() => {});
  };

  // Reset / restore quiz state whenever a new pill arrives. If the server
  // says the quiz was already answered, rebuild the revealed state.
  useEffect(() => {
    const qz = learn?.pill?.quiz;
    if (qz && qz.answered_index != null) {
      setQuizResult({
        picked: qz.answered_index, correct: qz.answered_correct,
        correct_index: qz.correct_index, explanation: qz.explanation || "",
        awarded: false, points: 0,
      });
    } else {
      setQuizResult(null);
    }
  }, [learn?.pill?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const answerQuiz = async (i) => {
    if (quizBusy || quizResult) return;
    setQuizBusy(true);
    try {
      const { data } = await api.post("/client/learn/quiz-answer", {
        pill_id: learn.pill.id, answer_index: i,
      });
      setQuizResult({ ...data, picked: i });
      if (data.correct && data.awarded) toast.success(t("learn.quizCorrectSp", { points: data.points }));
      else if (data.correct) toast.success(t("learn.quizCorrect"));
      if (data.streak_bonus) toast.success(t("learn.streakBonus", { points: data.streak_bonus, n: data.streak }));
      loadLearnStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setQuizBusy(false); }
  };

  const toggleActive = (idx) => {
    const next = active === idx ? null : idx;
    setActive(next);
    try { localStorage.setItem(LS_KEY, next === null ? "" : String(next)); } catch {}
    // Mark AI Coach as read when opening
    if (next === 0 && coach?.generated_at) {
      const ts = coach.generated_at || new Date().toISOString();
      setCoachReadAt(ts);
      try { localStorage.setItem(LS_COACH_READ, ts); } catch {}
    }
  };

  const refreshCoach = async (e) => {
    e?.stopPropagation();
    setCoachRefreshing(true);
    try {
      const { data } = await api.post(`/client/ai-coach/refresh?lang=${lang || "it"}`);
      setCoach(data);
      toast.success(t("aiCoach.refreshed"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setCoachRefreshing(false); }
  };

  const refreshLearn = async (e, category = null) => {
    e?.stopPropagation();
    setLearnRefreshing(true);
    try {
      const q = new URLSearchParams({ lang: lang || "it" });
      if (category) q.set("category", category);
      const { data } = await api.post(`/client/learn/next?${q.toString()}`);
      setLearn(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setLearnRefreshing(false); }
  };

  const healthScore = health?.score ?? 0;
  const medScore = med?.score ?? 0;
  const hCol = healthColors(healthScore);
  const mCol = medColors(medScore);
  // Only show the tip badge if tips are newer than the last time the user opened the chip
  const coachUnread = coach?.generated_at ? (!coachReadAt || coach.generated_at > coachReadAt) : false;
  const tipsCount = coachUnread ? (coach?.tips?.length || 0) : 0;

  const medDisabled = !med || med.meals_logged === 0;
  const learnDisabled = learnLoading && !learn;
  const healthDisabled = !health;
  const coachDisabled = coachLoading && !coach;

  const labelKey = { it: "label_it", en: "label_en", de: "label_de" }[lang || "it"] || "label_it";
  const medLabelsMap = MED_LABELS[lang || "it"] || MED_LABELS.it;
  const navigate = useNavigate();

  // Auto-close panel if it becomes disabled after data loads
  useEffect(() => {
    if (active === 1 && healthDisabled) setActive(null);
    if (active === 2 && medDisabled) setActive(null);
    if (active === 3 && learnDisabled) setActive(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthDisabled, medDisabled, learnDisabled]);

  return (
    <div data-testid="scores-row-container">
      <div className="grid grid-cols-4 gap-2" data-testid="scores-row">
        <ChipButton
          testId="chip-ai-coach"
          active={active === 0}
          onClick={() => toggleActive(0)}
          disabled={coachDisabled}
          iconNode={
            <div className="h-11 w-11 rounded-full bg-[#EAF3F4] dark:bg-[#22383a] flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#2C5254] dark:text-teal-200" />
            </div>
          }
          label={t("aiCoach.chipLabel")}
          subtitle={coachLoading && !coach
            ? t("common.loading")
            : (coach?.tips?.length
                ? t("aiCoach.chipSub", { n: coach.tips.length })
                : t("aiCoach.chipReady"))}
          badge={tipsCount}
        />
        <ChipButton
          testId="chip-health"
          active={active === 1}
          onClick={() => toggleActive(1)}
          disabled={healthDisabled}
          iconNode={<ScoreRing score={healthScore} colors={hCol} size={44} />}
          label={t("health.chipLabel")}
          subtitle={health
            ? (health.meals_logged === 0 ? t("health.chipEmpty") : healthLabelFor(healthScore, t))
            : t("common.loading")}
        />
        <ChipButton
          testId="chip-med"
          active={active === 2}
          onClick={() => toggleActive(2)}
          disabled={medDisabled}
          iconNode={<ScoreRing score={medScore} colors={mCol} size={44} />}
          label={t("med.chipLabel")}
          subtitle={med
            ? (med.meals_logged === 0
                ? t("med.chipEmpty")
                : t("med.chipDays", { days: med.days }))
            : t("common.loading")}
        />
        <ChipButton
          testId="chip-learn"
          active={active === 3}
          onClick={() => toggleActive(3)}
          disabled={learnDisabled}
          iconNode={
            <div className="h-11 w-11 rounded-full bg-[#E8F5EC] dark:bg-[#1e3a24] flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-[#1E5C2E] dark:text-green-200" />
            </div>
          }
          label={t("learn.chipLabel")}
          subtitle={learn?.pill?.category
            ? t(`learn.cat${learn.pill.category.charAt(0).toUpperCase()}${learn.pill.category.slice(1)}`)
            : (learnLoading ? t("common.loading") : t("learn.chipNew"))}
        />
      </div>

      {/* AI Coach panel */}
      {active === 0 && (
        <div
          className="mt-3 rounded-2xl border border-[#5A8B8F]/25 bg-gradient-to-br from-[#EAF3F4] to-[#DAECEC] dark:from-[#1e3a24] dark:to-[#1e2f31] dark:border-[#5A8B8F]/40 p-4"
          data-testid="panel-ai-coach"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="h-8 w-8 rounded-full bg-white/80 dark:bg-white/10 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4 text-[#2C5254] dark:text-teal-200" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-sm font-semibold text-[#2C5254] dark:text-teal-100 uppercase tracking-wide leading-tight">
                {t("aiCoach.title")}
              </p>
              <p className="text-[11px] text-[#2C5254]/75 dark:text-teal-200/70 truncate">
                {t("aiCoach.subtitle")}
              </p>
            </div>
            <button
              type="button"
              onClick={refreshCoach}
              disabled={coachRefreshing}
              data-testid="chip-ai-coach-refresh"
              className="h-8 w-8 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-[#2C5254] disabled:opacity-40 transition"
              aria-label={t("aiCoach.refresh")}
              title={t("aiCoach.refresh")}
            >
              <RefreshCw className={`h-4 w-4 ${coachRefreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
          {coachLoading && !coach ? (
            <div className="space-y-2">
              <div className="h-3 rounded bg-white/60 animate-pulse w-3/4" />
              <div className="h-3 rounded bg-white/60 animate-pulse w-5/6" />
              <div className="h-3 rounded bg-white/60 animate-pulse w-4/6" />
            </div>
          ) : coach ? (
            <>
              {coach.summary && (
                <p className="text-sm text-[#2C5254] dark:text-teal-100 leading-snug mb-2" data-testid="chip-ai-coach-summary">
                  {coach.summary}
                </p>
              )}
              <ul className="space-y-1.5" data-testid="chip-ai-coach-tips">
                {(coach.tips || []).map((tip, i) => (
                  <li
                    key={i}
                    className="text-sm text-[#1A1D1E] dark:text-slate-100 leading-snug"
                    data-testid={`chip-ai-coach-tip-${i}`}
                  >
                    {tip}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setChatOpen(true)}
                data-testid="chip-ai-coach-ask"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white text-sm font-semibold px-4 py-2 transition"
              >
                <MessageCircle className="h-4 w-4" />
                {t("aiCoach.askBtn")}
              </button>
            </>
          ) : null}
        </div>
      )}

      {/* Health panel */}
      {active === 1 && health && (
        <div
          className="mt-3 rounded-2xl border border-[#5A8B8F]/25 bg-gradient-to-br from-[#EAF3F4] to-[#DAECEC] dark:from-[#1e3a24] dark:to-[#1e2f31] dark:border-[#5A8B8F]/40 p-4"
          data-testid="panel-health"
        >
          <div className="flex items-center gap-3 mb-3">
            <ScoreRing score={healthScore} colors={hCol} size={54} stroke={4} />
            <div className="flex-1 min-w-0">
              <p className="font-heading text-sm font-semibold text-[#1A1D1E] dark:text-slate-100 uppercase tracking-wide leading-tight flex items-center gap-1.5">
                {t("health.title")}
                <button
                  type="button"
                  data-testid="health-info-btn"
                  onClick={(e) => { e.stopPropagation(); setHealthInfoOpen(true); }}
                  className="text-slate-400 hover:text-[#5A8B8F] transition"
                  aria-label={t("health.infoAria")}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                {health.meals_logged === 0 ? t("health.noMeals") : healthLabelFor(healthScore, t)}
              </p>
            </div>
          </div>
          {trend?.health?.series && (
            <div className="mb-3">
              <TrendBanner
                label={t("trend.last7days")}
                series={trend.health.series}
                delta={trend.health.delta}
                color="#5A8B8F"
                days={7}
                testId="trend-health"
              />
            </div>
          )}
          {health.meals_logged === 0 ? (
            <p className="text-[11px] text-slate-400 pt-1">{t("health.hint")}</p>
          ) : (
            <>
              {/* Sub-scores: Muscle / Heart / Energy — clickable to expand */}
              {health.subscores && (() => {
                const subs = [
                  { key: "muscle", emoji: "💪", data: health.subscores.muscle, tKey: "health.muscleTitle", accent: "#5A8B8F" },
                  { key: "heart", emoji: "❤️", data: health.subscores.heart, tKey: "health.heartTitle", accent: "#E85D1C" },
                  { key: "energy", emoji: "⚡", data: health.subscores.energy, tKey: "health.energyTitle", accent: "#E8951C" },
                ];
                const activeSub = openSubscore ? subs.find((s) => s.key === openSubscore) : null;
                return (
                  <div className="mb-3" data-testid="panel-health-subscores">
                    <div className="grid grid-cols-3 gap-2">
                      {subs.map((s) => {
                        const isActive = openSubscore === s.key;
                        const scoreVal = typeof s.data === "object" ? s.data?.score ?? 0 : s.data ?? 0;
                        return (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => setOpenSubscore(isActive ? null : s.key)}
                            aria-pressed={isActive}
                            data-testid={`subscore-${s.key}`}
                            className={`rounded-xl px-2 py-2 flex flex-col items-center gap-1 border transition text-center ${
                              isActive
                                ? "bg-white dark:bg-white/10 border-[#5A8B8F] shadow-sm"
                                : "bg-white/60 dark:bg-white/5 border-white/40 dark:border-white/10 hover:border-[#5A8B8F]/50"
                            }`}
                          >
                            <ScoreRing score={scoreVal} colors={{ ring: s.accent, text: s.accent }} size={40} stroke={3} />
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#1A1D1E] dark:text-slate-100 flex items-center gap-0.5 justify-center leading-tight">
                              <span className="text-xs" aria-hidden>{s.emoji}</span>
                              {t(s.tKey)}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                    {activeSub && activeSub.data?.components && (
                      <div
                        className="mt-2 rounded-xl bg-white/70 dark:bg-white/5 border border-white/50 dark:border-white/10 p-3"
                        data-testid={`subscore-${activeSub.key}-detail`}
                      >
                        <p className="text-[11px] font-bold uppercase tracking-wider text-[#1A1D1E] dark:text-slate-100 mb-2 flex items-center gap-1">
                          <span className="text-sm" aria-hidden>{activeSub.emoji}</span>
                          {t(activeSub.tKey)} · {activeSub.data.score}/100
                        </p>
                        <div className="space-y-1.5">
                          {activeSub.data.components.map((c) => {
                            const label = c[labelKey] || c.label_it;
                            const pct = Math.max(0, Math.min(100, c.pct || 0));
                            const good = pct >= 70;
                            return (
                              <div key={c.key} data-testid={`sub-comp-${activeSub.key}-${c.key}`}>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold min-w-[32px] justify-center ${
                                    good ? "bg-green-100 text-green-800" : pct >= 40 ? "bg-slate-100 text-slate-600" : "bg-red-100 text-red-700"
                                  }`}>
                                    {pct}%
                                  </span>
                                  <span className="flex-1 text-[#1A1D1E] dark:text-slate-100 truncate">
                                    {label}
                                    <span className="text-slate-400 dark:text-slate-500 font-normal"> · {c.weight}%</span>
                                  </span>
                                  <span className="text-xs text-slate-700 dark:text-slate-200 font-semibold tabular-nums shrink-0">
                                    <span className={valueColorClass(c.value, c.target, c.sign !== "-")}>{c.value}</span>
                                    <span className="text-slate-500 dark:text-slate-400 font-normal"> / {c.target}{c.unit ? ` ${c.unit}` : ""}</span>
                                  </span>
                                </div>
                                {c.key === "salt" && Number(c.value) > 0 && (
                                  <p className="text-[10px] text-slate-400 dark:text-slate-500 italic mt-0.5 ml-9">
                                    ≈ {(() => {
                                      const tsp = Number(c.value) / 5;
                                      if (tsp < 0.25) return t("health.saltPinch");
                                      if (tsp < 0.75) return t("health.saltHalfTsp");
                                      const n = Math.round(tsp * 2) / 2; // 0.5 step
                                      const whole = Math.floor(n);
                                      const frac = n - whole;
                                      const num = whole + (frac === 0.5 ? "½" : "");
                                      return `${num || "½"} ${tsp < 1.5 ? t("health.saltTsp") : t("health.saltTspPl")}`;
                                    })()}
                                  </p>
                                )}
                                <div className="h-1 rounded-full bg-slate-200/50 dark:bg-white/10 overflow-hidden mt-1">
                                  <div
                                    className={`h-full rounded-full transition-all ${good ? "bg-emerald-500" : pct >= 40 ? "bg-[#5A8B8F]" : "bg-[#E85D1C]"}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="space-y-1.5" data-testid="panel-health-breakdown">
                {(health.components || []).map((comp) => {
                  const isPos = comp.sign === "+";
                  // Always show earned points (`+X/max`) so the breakdown
                  // sums to the total score. Limit-items (sat fat, sugar,
                  // salt) earn points by staying under the daily threshold;
                  // colour tells the user whether that specific item was on
                  // track (green), borderline (amber) or blown (red).
                  const shown = `+${comp.points}`;
                  const ratio = comp.max ? comp.points / comp.max : 0;
                  const pillCls = isPos
                    ? (ratio >= 0.7 ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600")
                    : (ratio >= 0.7 ? "bg-green-100 text-green-800"
                       : ratio > 0 ? "bg-amber-100 text-amber-800"
                       : "bg-red-100 text-red-700");
                  return (
                    <div key={comp.key} className="flex items-center gap-2 text-sm" data-testid={`panel-health-comp-${comp.key}`}>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold min-w-[52px] justify-center ${pillCls}`}>
                        {shown}<span className="opacity-60 font-medium">/{comp.max}</span>
                      </span>
                      <span className="flex-1 text-[#1A1D1E] dark:text-slate-100">{comp[labelKey]}</span>
                      <span className="text-xs text-slate-700 dark:text-slate-200 font-semibold tabular-nums">
                        <span className={valueColorClass(comp.value, comp.target, isPos)}>{comp.value}</span>
                        <span className="text-slate-500 dark:text-slate-400 font-normal"> / {comp.target}{comp.unit === "porzioni" ? "" : ` ${comp.unit}`}</span>
                      </span>
                    </div>
                  );
                })}
                <p className="text-[11px] text-slate-400 pt-1">{t("health.hint")}</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Mediterranean panel */}
      {active === 2 && med && !medDisabled && (
        <div
          className="mt-3 rounded-2xl border border-[#5A8B8F]/25 bg-gradient-to-br from-[#EAF3F4] to-[#DAECEC] dark:from-[#1e3a24] dark:to-[#1e2f31] dark:border-[#5A8B8F]/40 p-4"
          data-testid="panel-med"
        >
          <div className="flex items-center gap-3 mb-3">
            <ScoreRing score={medScore} colors={mCol} size={54} stroke={4} />
            <div className="flex-1 min-w-0">
              <p className="font-heading text-sm font-semibold text-[#1A1D1E] dark:text-slate-100 uppercase tracking-wide leading-tight flex items-center gap-1.5">
                <Waves className="h-3.5 w-3.5 text-[#0EA5E9]" />
                {t("med.title")}
                <button
                  type="button"
                  data-testid="med-info-btn"
                  onClick={(e) => { e.stopPropagation(); setMedInfoOpen(true); }}
                  className="text-slate-400 hover:text-[#5A8B8F] transition"
                  aria-label={t("med.infoAria")}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                {t("med.subtitle", { days: med.days })}
              </p>
            </div>
          </div>
          {trend?.med?.series && (
            <div className="mb-3">
              <TrendBanner
                label={t("trend.last7days")}
                series={trend.med.series}
                delta={trend.med.delta}
                color="#0EA5E9"
                days={7}
                testId="trend-med"
              />
            </div>
          )}
          <div className="space-y-1.5" data-testid="panel-med-breakdown">
            {(med.components || []).map((comp) => {
              const isPos = comp.sign === "+";
              const good = isPos ? comp.points >= comp.max * 0.7 : comp.points === 0;
              const pillCls = good
                ? "bg-green-100 text-green-800"
                : isPos ? "bg-slate-100 text-slate-600" : "bg-red-100 text-red-700";
              const shown = isPos ? `+${comp.points}` : (comp.points === 0 ? "0" : `${comp.points}`);
              const logs = Array.isArray(comp.logs) ? comp.logs : [];
              const expandable = logs.length > 0;
              const isOpen = openMedRow === comp.key;
              return (
                <div key={comp.key} data-testid={`panel-med-comp-${comp.key}`}>
                  <button
                    type="button"
                    disabled={!expandable}
                    onClick={() => setOpenMedRow(isOpen ? null : comp.key)}
                    aria-expanded={isOpen}
                    aria-controls={`med-comp-logs-${comp.key}`}
                    data-testid={`panel-med-comp-toggle-${comp.key}`}
                    className={`w-full flex items-center gap-2 text-sm text-left rounded-lg px-1 py-0.5 -mx-1 transition ${
                      expandable ? "hover:bg-white/40 dark:hover:bg-white/5 cursor-pointer" : "cursor-default"
                    }`}
                  >
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold min-w-[42px] justify-center ${pillCls}`}>
                      {shown}
                    </span>
                    <span className="flex-1 text-[#1A1D1E] dark:text-slate-100 flex items-center gap-1 min-w-0">
                      <span className="truncate">{medLabelsMap[comp.key] || comp.key}</span>
                      {expandable && (
                        <span
                          className={`text-slate-400 text-[10px] shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                          aria-hidden
                        >▸</span>
                      )}
                    </span>
                    <span className="text-xs text-slate-700 dark:text-slate-200 font-semibold tabular-nums">
                      <span className={valueColorClass(comp.value, comp.target, isPos)}>{comp.value}</span><span className="text-slate-500 dark:text-slate-400 font-normal"> / {comp.target}</span>
                    </span>
                  </button>
                  {isOpen && expandable && (
                    <div
                      id={`med-comp-logs-${comp.key}`}
                      data-testid={`panel-med-comp-logs-${comp.key}`}
                      className="mt-1.5 ml-[50px] mr-1 rounded-lg bg-white/70 dark:bg-white/5 border border-white/50 dark:border-white/10 p-2 space-y-1"
                    >
                      {logs.map((lg, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-[11px] leading-tight"
                          data-testid={`panel-med-log-${comp.key}-${i}`}
                        >
                          <span className="text-slate-400 tabular-nums shrink-0 w-[52px]">{lg.date}</span>
                          <span className="flex-1 min-w-0" title={lg.parent ? `${lg.description} · ${lg.parent}` : lg.description}>
                            <span className="text-slate-700 dark:text-slate-200 truncate block">
                              {lg.description}
                            </span>
                            {lg.parent && lg.parent !== lg.description && (
                              <span className="text-[9px] text-slate-400 dark:text-slate-500 truncate block leading-tight">
                                ↳ {lg.parent}
                              </span>
                            )}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400 tabular-nums shrink-0">
                            {lg.grams != null ? `${lg.grams}g` : `${lg.portions}×`}
                          </span>
                        </div>
                      ))}
                      {!isPos && (
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 italic pt-1 border-t border-white/40 dark:border-white/10">
                          {t("med.penaltyHint") || "Ogni porzione oltre 1/settimana toglie punti (max −6)."}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {medTipKey(med) && (
              <button
                type="button"
                onClick={() => navigate(`/app/ricette?medq=${medTipKey(med)}`)}
                className="w-full flex items-start gap-2 rounded-2xl bg-[#E8951C]/10 border border-[#E8951C]/25 px-3 py-2.5 mt-1 text-left hover:bg-[#E8951C]/20 active:scale-[0.99] transition"
                data-testid="med-daily-tip"
              >
                <Lightbulb className="h-4 w-4 text-[#c47a10] mt-0.5 shrink-0" />
                <span className="text-xs leading-relaxed text-slate-700 dark:text-slate-200 flex-1">
                  <span className="font-semibold text-[#9a6410] dark:text-[#E8951C]">{t("med.tipTitle")}: </span>
                  {t(`med.tips.${medTipKey(med)}`)}
                  <span className="block mt-0.5 font-semibold text-[#2C5254] dark:text-teal-300">{t("med.tipCta")} →</span>
                </span>
              </button>
            )}
            <p className="text-[11px] text-slate-400 pt-1">{t("med.hint")}</p>
          </div>
        </div>
      )}

      {/* Learn panel */}
      {active === 3 && (
        <div
          className="mt-3 rounded-2xl border border-[#5A8B8F]/25 bg-gradient-to-br from-[#EAF3F4] to-[#DAECEC] dark:from-[#1e3a24] dark:to-[#1e2f31] dark:border-[#5A8B8F]/40 p-4"
          data-testid="panel-learn"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="h-8 w-8 rounded-full bg-white/80 dark:bg-white/10 flex items-center justify-center shrink-0">
              <BookOpen className="h-4 w-4 text-[#1E5C2E] dark:text-green-200" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-sm font-semibold text-[#1E5C2E] dark:text-green-100 uppercase tracking-wide leading-tight">
                {t("learn.title")}
              </p>
              <p className="text-[11px] text-[#1E5C2E]/75 dark:text-green-200/70 truncate">
                {t("learn.subtitle")}
              </p>
            </div>
            {!(learn && learn.index >= learn.max_index) && (
            <button
              type="button"
              onClick={(e) => refreshLearn(e)}
              disabled={learnRefreshing}
              data-testid="chip-learn-refresh"
              className="h-8 w-8 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-[#1E5C2E] disabled:opacity-40 transition"
              aria-label={t("learn.next")}
              title={t("learn.next")}
            >
              <RefreshCw className={`h-4 w-4 ${learnRefreshing ? "animate-spin" : ""}`} />
            </button>
            )}
          </div>

          <LearnStatsStrip stats={learnStats} t={t} />
          {learnStats?.review_due > 0 && (
            <LearnReviewCard key={`rev-${lang}`} due={learnStats.review_due} lang={lang} t={t} onAnswered={loadLearnStats} />
          )}

          {learnLoading && !learn ? (
            <div className="space-y-2">
              <div className="h-3 rounded bg-white/60 animate-pulse w-3/4" />
              <div className="h-3 rounded bg-white/60 animate-pulse w-5/6" />
              <div className="h-3 rounded bg-white/60 animate-pulse w-4/6" />
            </div>
          ) : learn?.pill ? (
            <div data-testid="learn-pill">
              <p className="font-heading text-base font-bold text-[#1A1D1E] dark:text-slate-50 leading-snug mb-2 flex items-start gap-2">
                <span className="text-xl shrink-0" aria-hidden>{learn.pill.emoji || "📚"}</span>
                <span>{learn.pill.title}</span>
              </p>
              <p className="text-sm text-[#1A1D1E] dark:text-slate-100 leading-relaxed mb-2">
                {learn.pill.body}
              </p>
              {learn.pill.takeaway && (
                <p className="text-sm font-semibold text-[#1E5C2E] dark:text-green-200 italic mb-3">
                  “{learn.pill.takeaway}”
                </p>
              )}
              {learn.pill.quiz && (
                <div className="mb-3 rounded-xl bg-white/60 dark:bg-white/5 border border-[#5A8B8F]/20 p-3" data-testid="learn-quiz">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#1E5C2E]/70 dark:text-green-200/70 mb-1">
                    {t("learn.quizTitle")}
                  </p>
                  <p className="text-sm font-semibold text-[#1A1D1E] dark:text-slate-50 leading-snug mb-2">
                    {learn.pill.quiz.question}
                  </p>
                  <div className="space-y-1.5">
                    {learn.pill.quiz.options.map((opt, i) => {
                      const answered = !!quizResult;
                      const isPicked = quizResult?.picked === i;
                      const isCorrect = quizResult?.correct_index === i;
                      let cls = "bg-white/80 dark:bg-white/10 text-[#1A1D1E] dark:text-slate-100 border-transparent hover:bg-white";
                      if (answered && isCorrect) cls = "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200 border-emerald-400";
                      else if (answered && isPicked && !isCorrect) cls = "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 border-rose-400";
                      else if (answered) cls = "bg-white/40 dark:bg-white/5 text-slate-400 dark:text-slate-500 border-transparent";
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => answerQuiz(i)}
                          disabled={answered || quizBusy}
                          data-testid={`learn-quiz-option-${i}`}
                          className={`w-full text-left rounded-lg border px-3 py-2 text-sm font-medium transition ${cls} disabled:cursor-default`}
                        >
                          {answered && isCorrect ? "✓ " : answered && isPicked && !isCorrect ? "✗ " : ""}{opt}
                        </button>
                      );
                    })}
                  </div>
                  {quizResult && (
                    <p className={`text-xs mt-2 leading-snug ${quizResult.correct ? "text-emerald-700 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}`} data-testid="learn-quiz-feedback">
                      {quizResult.correct
                        ? (quizResult.awarded ? t("learn.quizCorrectSp", { points: quizResult.points }) : t("learn.quizCorrect"))
                        : t("learn.quizWrong")}
                      {quizResult.explanation ? ` ${quizResult.explanation}` : ""}
                    </p>
                  )}
                </div>
              )}
              {learn.pill.category === "basics" && learn.pill.basics_index != null && (
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#1E5C2E]/80 dark:text-green-200/80 mb-2" data-testid="learn-basics-progress">
                  {t("learn.basicsProgress", { n: learn.pill.basics_index + 1, total: learn.pill.basics_total })}
                </p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {(() => {
                  const seen = learn.seen_categories || [];
                  const cats = [...(seen.includes("basics") ? ["basics"] : []), "nutrition", "health", "recipes", "myths"];
                  return cats.map((cat) => {
                  const isActive = learn.pill.category === cat;
                  const limitReached = learn.index >= learn.max_index;
                  const canTap = seen.includes(cat) || !limitReached;
                  const labelKey = {
                    basics: "learn.catBasics",
                    nutrition: "learn.catNutrition",
                    health: "learn.catHealth",
                    recipes: "learn.catRecipes",
                    myths: "learn.catMyths",
                  }[cat];
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={(e) => refreshLearn(e, cat)}
                      disabled={learnRefreshing || !canTap}
                      data-testid={`learn-cat-${cat}`}
                      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition ${
                        isActive
                          ? "bg-[#3AA35B] text-white"
                          : "bg-white/70 dark:bg-white/10 text-[#1E5C2E] dark:text-green-200 hover:bg-white"
                      } disabled:opacity-50`}
                    >
                      {t(labelKey)}
                    </button>
                  );
                  });
                })()}
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#1E5C2E] dark:text-green-200">{t("learn.empty")}</p>
          )}
        </div>
      )}

      <AICoachChatDialog open={chatOpen} onOpenChange={setChatOpen} />

      {medInfoOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setMedInfoOpen(false)}
          data-testid="med-info-modal"
        >
          <div
            className="bg-white dark:bg-slate-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5A8B8F] font-semibold">{t("med.infoTag")}</p>
                <h3 className="font-heading text-lg font-semibold text-[#1A1D1E] dark:text-white mt-0.5">{t("med.infoTitle")}</h3>
              </div>
              <button
                onClick={() => setMedInfoOpen(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-2xl leading-none px-1"
                data-testid="med-info-close"
                aria-label="close"
              >×</button>
            </div>

            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {t("med.infoBody")}
            </p>

            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{t("med.infoTargetsTitle")}</p>
              <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
                <li className="flex justify-between"><span>{medLabelsMap.olive_oil}</span><span className="text-slate-500">5×/wk · 10g</span></li>
                <li className="flex justify-between"><span>{medLabelsMap.fish}</span><span className="text-slate-500">2×/wk · 150g</span></li>
                <li className="flex justify-between"><span>{medLabelsMap.vegetables}</span><span className="text-slate-500">10×/wk · 80g</span></li>
                <li className="flex justify-between"><span>{medLabelsMap.fruits}</span><span className="text-slate-500">7×/wk · 150g</span></li>
                <li className="flex justify-between"><span>{medLabelsMap.legumes}</span><span className="text-slate-500">3×/wk · 150g</span></li>
                <li className="flex justify-between"><span>{medLabelsMap.whole_grains}</span><span className="text-slate-500">5×/wk · 80g</span></li>
                <li className="flex justify-between"><span>{medLabelsMap.nuts_seeds}</span><span className="text-slate-500">3×/wk · 20g</span></li>
                <li className="flex justify-between"><span>{medLabelsMap.dairy_fermented}</span><span className="text-slate-500">5×/wk · 125g</span></li>
                <li className="flex justify-between text-red-600"><span>{medLabelsMap.red_meat}</span><span>≤1×/wk · 100g</span></li>
                <li className="flex justify-between text-red-600"><span>{medLabelsMap.processed_meat}</span><span>≤1×/wk · 50g</span></li>
              </ul>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-[#5A8B8F]/8 border border-[#5A8B8F]/20 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              💡 {t("med.infoTip")}
            </div>

            <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">{t("med.infoSource")}</p>

            <button
              onClick={() => setMedInfoOpen(false)}
              className="mt-4 w-full h-10 rounded-full bg-[#1A1D1E] text-white text-sm font-semibold hover:bg-[#2C5254] transition"
              data-testid="med-info-ok"
            >
              {t("common.close") || "OK"}
            </button>
          </div>
        </div>
      )}

      {healthInfoOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setHealthInfoOpen(false)}
          data-testid="health-info-modal"
        >
          <div
            className="bg-white dark:bg-slate-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5A8B8F] font-semibold">{t("health.infoTag")}</p>
                <h3 className="font-heading text-lg font-semibold text-[#1A1D1E] dark:text-white mt-0.5">{t("health.infoTitle")}</h3>
              </div>
              <button
                onClick={() => setHealthInfoOpen(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-2xl leading-none px-1"
                data-testid="health-info-close"
                aria-label="close"
              >×</button>
            </div>

            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {t("health.infoBody")}
            </p>

            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-300 mb-2">{t("health.infoPositiveTitle")}</p>
              <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
                <li className="flex justify-between"><span>{t("health.infoProtein")}</span><span className="text-slate-500 tabular-nums">+25</span></li>
                <li className="flex justify-between"><span>{t("health.infoFiber")}</span><span className="text-slate-500 tabular-nums">+20</span></li>
                <li className="flex justify-between"><span>{t("health.infoFV")}</span><span className="text-slate-500 tabular-nums">+20</span></li>
                <li className="flex justify-between"><span>{t("health.infoWater")}</span><span className="text-slate-500 tabular-nums">+5</span></li>
              </ul>
            </div>

            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-red-700 dark:text-red-300 mb-2">{t("health.infoNegativeTitle")}</p>
              <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
                <li className="flex justify-between"><span>{t("health.infoSatFat")}</span><span className="text-slate-500 tabular-nums">+10</span></li>
                <li className="flex justify-between"><span>{t("health.infoSugar")}</span><span className="text-slate-500 tabular-nums">+10</span></li>
                <li className="flex justify-between"><span>{t("health.infoSalt")}</span><span className="text-slate-500 tabular-nums">+10</span></li>
              </ul>
              <p className="text-[10px] text-slate-400 mt-1.5 italic">{t("health.infoNegativeHint")}</p>
            </div>

            <div className="mt-4" data-testid="health-info-subscores">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-[#2C5254] dark:text-[#8FBDC0] mb-2">{t("health.infoSubTitle")}</p>
              <div className="space-y-2.5">
                {[
                  ["💪", t("health.muscleTitle"), t("health.infoMuscleDesc"), "muscle"],
                  ["❤️", t("health.heartTitle"), t("health.infoHeartDesc"), "heart"],
                  ["⚡", t("health.energyTitle"), t("health.infoEnergyDesc"), "energy"],
                ].map(([emoji, name, desc, key]) => (
                  <div key={key} className="rounded-lg bg-slate-50 dark:bg-white/5 p-2.5" data-testid={`health-info-sub-${key}`}>
                    <p className="text-xs font-bold text-[#1A1D1E] dark:text-white flex items-center gap-1.5">
                      <span aria-hidden>{emoji}</span> {name}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mt-0.5">{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-[#5A8B8F]/8 border border-[#5A8B8F]/20 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              💡 {t("health.infoTip")}
            </div>

            <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">{t("health.infoSource")}</p>

            <button
              onClick={() => setHealthInfoOpen(false)}
              className="mt-4 w-full h-10 rounded-full bg-[#1A1D1E] text-white text-sm font-semibold hover:bg-[#2C5254] transition"
              data-testid="health-info-ok"
            >
              {t("common.close") || "OK"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
